'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────

type InputType = 'crypto' | 'url' | 'email' | 'unknown';

// Unified crypto response schema
interface CryptoResult {
  title: string;
  riskLevel: 'High' | 'Medium' | 'Low' | 'Unknown';
  summary: string;
  details: Record<string, string | number | null>;
  provider: string;
  chain: string;
  queryType: 'address' | 'transaction';
  explorerUrl: string;
  query: string;
  error?: string;
}

interface UrlResult {
  type: 'url';
  query: string;
  ipqs?: {
    domain?: string | null;
    ipAddress?: string | null;
    country?: string | null;
    server?: string | null;
    pageTitle?: string | null;
    domainAgeHuman?: string | null;
    domainAgeTimestamp?: number | null;
    riskScore?: number | null;
    dnsValid?: boolean;
    sslValid?: boolean;
    statusCode?: number | null;
    malware?: boolean;
    phishing?: boolean;
    suspicious?: boolean;
    unsafe?: boolean;
    spamming?: boolean;
    category?: string | null;
    hasHttps?: boolean;
  } | null;
  virusTotal?: {
    malicious: number;
    suspicious: number;
    harmless: number;
    undetected: number;
    totalEngines: number;
    vtLink?: string;
  } | null;
  error?: string;
}

interface EmailResult {
  type: 'email';
  from?: string | null;
  to?: string | null;
  subject?: string | null;
  date?: string | null;
  messageId?: string | null;
  replyTo?: string | null;
  returnPath?: string | null;
  xMailer?: string | null;
  senderIp?: string | null;
  spf?: string | null;
  dkim?: string | null;
  dmarc?: string | null;
  receivedHops?: Array<{ raw: string; from?: string; by?: string; ip?: string; timestamp?: string }>;
  anomalies?: string[];
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH';
  error?: string;
}

type AnalysisResult = CryptoResult | UrlResult | EmailResult | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectInputType(value: string): InputType {
  const v = value.trim();
  if (!v) return 'unknown';

  if (
    /^(1|3)[a-zA-Z0-9]{25,34}$/.test(v) ||
    /^bc1[a-zA-Z0-9]{25,87}$/.test(v) ||
    /^0x[a-fA-F0-9]{40,64}$/.test(v) ||
    /^T[a-zA-Z0-9]{33}$/.test(v) ||
    /^[a-fA-F0-9]{64}$/.test(v) ||
    /^[LM3][a-zA-Z0-9]{26,33}$/.test(v) ||
    /^ltc1[a-zA-Z0-9]{25,87}$/.test(v) ||
    /^(bitcoincash:)?[qp][a-z0-9]{41}$/.test(v) ||
    /^r[a-zA-Z0-9]{24,34}$/.test(v) ||
    /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v) ||
    /^D[a-zA-Z0-9]{33}$/.test(v) ||
    /^X[a-zA-Z0-9]{33}$/.test(v) ||
    /^bnb[a-zA-Z0-9]{38}$/.test(v) ||
    /^cosmos[a-zA-Z0-9]{38}$/.test(v) ||
    /^addr1[a-zA-Z0-9]{50,}$/.test(v)
  ) return 'crypto';

  if (/^(https?:\/\/|www\.)[^\s]+/.test(v) || /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/.test(v)) return 'url';
  if (/^(Received:|From:|Return-Path:|MIME-Version:|Date:)/im.test(v)) return 'email';

  return 'unknown';
}

function calcSecurityScore(
  malicious: number,
  suspicious: number,
  totalEngines: number,
  hasHttps: boolean,
  hasTls: boolean,
  creationDate: number | null | undefined
): number {
  let score = 100;
  if (totalEngines > 0) {
    const maliciousRatio = malicious / totalEngines;
    const suspiciousRatio = suspicious / totalEngines;
    score -= Math.round(maliciousRatio * 60);
    score -= Math.round(suspiciousRatio * 20);
  }
  if (!hasHttps) score -= 15;
  if (!hasTls) score -= 10;
  if (creationDate) {
    const diffDays = (Date.now() - creationDate * 1000) / (1000 * 60 * 60 * 24);
    if (diffDays < 30) score -= 20;
    else if (diffDays < 180) score -= 10;
    else if (diffDays < 365) score -= 5;
  }
  return Math.max(0, Math.min(100, score));
}

function riskBadgeClasses(level: string): { dot: string; text: string; border: string; bg: string } {
  const l = level?.toLowerCase();
  if (l === 'high') return { dot: 'bg-red-400', text: 'text-red-400', border: 'border-red-900/50', bg: 'bg-red-950/10' };
  if (l === 'medium') return { dot: 'bg-yellow-400', text: 'text-yellow-400', border: 'border-yellow-900/50', bg: 'bg-yellow-950/10' };
  return { dot: 'bg-green-400', text: 'text-green-400', border: 'border-green-900/30', bg: 'bg-green-950/5' };
}

const INPUT_EXAMPLES = [
  { label: 'BTC Address', value: '1A1zP1eP5QGefi2DMPTfTL5SLmv7Divf', type: 'crypto' },
  { label: 'ETH Address', value: '0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe', type: 'crypto' },
  { label: 'Website URL', value: 'https://example.com', type: 'url' },
  { label: 'Email Headers', value: 'Received: from mail.example.com...', type: 'email' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ label, pass }: { label: string; pass: boolean | null | undefined }) {
  if (pass == null) return <span className="bracket-tag text-zinc-600">{label}: —</span>;
  return (
    <span className={`bracket-tag ${pass ? 'text-green-400' : 'text-red-400'}`}>
      {label}: {pass ? 'PASS' : 'FAIL'}
    </span>
  );
}

function DataRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2.5 border-b border-zinc-800 last:border-0">
      <span className="bracket-tag text-zinc-500 shrink-0 w-44">{label}</span>
      <span className={`text-sm text-foreground break-all ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function SectionHeader({ tag, title }: { tag: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-6 h-px bg-primary shrink-0" />
      <span className="bracket-tag text-primary text-xs">{tag}</span>
      <div className="flex-1 h-px bg-zinc-800" />
      <span className="text-xs font-black tracking-widest uppercase text-zinc-400">{title}</span>
    </div>
  );
}

function CryptoResultPanel({ result }: { result: CryptoResult }) {
  const risk = riskBadgeClasses(result.riskLevel);
  const chainLabel = result.chain?.toUpperCase() || 'CRYPTO';
  const isAddress = result.queryType === 'address';

  const detailEntries = Object.entries(result.details).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );

  return (
    <div className="space-y-5">
      <div className={`glass-card border ${risk.border} ${risk.bg} p-5`}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`w-2 h-2 rounded-full ${risk.dot} threat-pulse shrink-0`} />
              <span className={`bracket-tag font-black text-base ${risk.text}`}>
                RISK: {result.riskLevel.toUpperCase()}
              </span>
              <span className="bracket-tag text-zinc-500">[{chainLabel}]</span>
              <span className="bracket-tag text-zinc-600 text-xs">via ForensicDesk</span>
            </div>
            <h2 className="text-lg font-black tracking-tight text-foreground mb-1">{result.title}</h2>
            <p className="font-mono text-xs text-zinc-400 break-all">{result.query}</p>
          </div>
          {result.explorerUrl && (
            <a
              href={result.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 border border-zinc-700 hover:border-primary text-zinc-400 hover:text-foreground px-4 py-2 bracket-tag transition-colors duration-200 whitespace-nowrap text-xs shrink-0 self-start"
            >
              <Icon name="ArrowTopRightOnSquareIcon" size={12} />
              VIEW IN EXPLORER
            </a>
          )}
        </div>
      </div>

      <div className="glass-card border border-zinc-800 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icon name="ShieldExclamationIcon" size={16} className="text-primary shrink-0" />
          <div>
            <p className="bracket-tag text-zinc-400 text-xs mb-0.5">[CROSS-REFERENCE]</p>
            <p className="text-sm text-foreground font-medium">Check this asset against the ForensicDesk Threat Registry</p>
          </div>
        </div>
        <a
          href={`/threat-registry?q=${encodeURIComponent(result.query)}`}
          className="flex items-center gap-2 border border-primary/50 hover:border-primary text-primary hover:bg-primary/5 px-4 py-2 bracket-tag transition-all duration-200 whitespace-nowrap text-xs shrink-0 min-h-[44px]"
        >
          <Icon name="MagnifyingGlassIcon" size={12} />
          SEARCH THREAT REGISTRY
        </a>
      </div>

      <div className="glass-card border border-zinc-800 p-5">
        <SectionHeader tag={isAddress ? '[ADDRESS]' : '[TRANSACTION]'} title="SUMMARY" />
        <p className="text-sm text-zinc-300 leading-relaxed">{result.summary}</p>
      </div>

      {detailEntries.length > 0 && (
        <div className="glass-card border border-zinc-800 p-5">
          <SectionHeader tag="[DATA]" title="CHAIN DETAILS" />
          <div>
            {detailEntries.map(([key, val]) => (
              <DataRow
                key={key}
                label={key.toUpperCase()}
                value={String(val)}
                mono={
                  key.toLowerCase().includes('address') ||
                  key.toLowerCase().includes('hash') ||
                  key.toLowerCase().includes('from') ||
                  key.toLowerCase().includes('to') ||
                  key.toLowerCase() === 'hash'
                }
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Icon name="ShieldCheckIcon" size={12} className="text-zinc-600" />
        <span className="bracket-tag text-zinc-600 text-xs">
          DATA SOURCE: FORENSICDESK // SERVER-SIDE ONLY // NO KEY EXPOSED
        </span>
      </div>
    </div>
  );
}

function UrlResultPanel({ result }: { result: UrlResult }) {
  const vt = result.virusTotal;
  const ipqs = result.ipqs;

  const isMalicious = (vt?.malicious ?? 0) > 0 || ipqs?.malware || ipqs?.phishing || ipqs?.unsafe;
  const isSuspicious = (vt?.suspicious ?? 0) > 0 || ipqs?.suspicious;

  const hasHttps = ipqs?.hasHttps ?? result.query.startsWith('https://');
  const sslValid = ipqs?.sslValid ?? false;
  const domainAge = ipqs?.domainAgeHuman ?? 'N/A';

  const securityScore = (() => {
    if (ipqs?.riskScore != null) {
      return Math.max(0, Math.min(100, 100 - ipqs.riskScore));
    }
    if (vt) {
      return calcSecurityScore(vt.malicious, vt.suspicious, vt.totalEngines, hasHttps, hasHttps, null);
    }
    return hasHttps ? 60 : 40;
  })();

  const riskLevel = isMalicious ? 'High Risk' : isSuspicious ? 'Medium Risk' : securityScore >= 70 ? 'Low Risk' : 'Medium Risk';
  const riskColor = isMalicious ? 'text-red-400' : isSuspicious || securityScore < 70 ? 'text-yellow-400' : 'text-green-400';
  const riskBg = isMalicious ? 'border-red-900/50 bg-red-950/10' : isSuspicious || securityScore < 70 ? 'border-yellow-900/50 bg-yellow-950/10' : 'border-green-900/30 bg-green-950/5';
  const riskDot = isMalicious ? 'bg-red-400' : isSuspicious || securityScore < 70 ? 'bg-yellow-400' : 'bg-green-400';
  const scoreColor = securityScore >= 70 ? 'text-green-400' : securityScore >= 40 ? 'text-yellow-400' : 'text-red-400';
  const riskSummary = isMalicious ? 'Malicious content detected by security engines.' : isSuspicious ? 'Suspicious signals detected — proceed with caution.' : 'No major security threats detected.';

  const positiveIndicators: string[] = [];
  if (hasHttps) positiveIndicators.push('Valid HTTPS connection');
  if (sslValid) positiveIndicators.push('SSL certificate is valid');
  if (domainAge && domainAge !== 'N/A') positiveIndicators.push(`Domain established: ${domainAge}`);
  if (vt && vt.malicious === 0 && vt.totalEngines > 0) positiveIndicators.push(`Clean across all ${vt.totalEngines} security engines`);
  if (ipqs && !ipqs.malware && !ipqs.phishing && !ipqs.unsafe) positiveIndicators.push('IPQS: No malware, phishing, or unsafe flags');

  let recommendation = 'Normal caution advised.';
  if (isMalicious) recommendation = 'Do not visit this URL. High threat level confirmed.';
  else if (isSuspicious) recommendation = 'Exercise caution. Suspicious signals detected.';
  else if (securityScore >= 80) recommendation = 'Normal caution advised. No significant threats detected.';
  else if (securityScore >= 60) recommendation = 'Proceed with caution. Some risk indicators present.';

  const riskFlags: string[] = [];
  if (ipqs?.malware) riskFlags.push('MALWARE');
  if (ipqs?.phishing) riskFlags.push('PHISHING');
  if (ipqs?.suspicious) riskFlags.push('SUSPICIOUS');
  if (ipqs?.unsafe) riskFlags.push('UNSAFE');
  if (ipqs?.spamming) riskFlags.push('SPAMMING');

  return (
    <div className="space-y-5">
      <div className={`glass-card border ${riskBg} p-5`}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`w-2 h-2 rounded-full ${riskDot} threat-pulse shrink-0`} />
              <span className={`bracket-tag font-black text-base ${riskColor}`}>{riskLevel.toUpperCase()}</span>
              <span className="bracket-tag text-zinc-600 text-xs">via ForensicDesk</span>
            </div>
            <p className="text-sm text-zinc-300 mb-1">{riskSummary}</p>
            <p className="font-mono text-xs text-zinc-500 break-all mt-2">{result.query}</p>
          </div>
          <div className="flex flex-col items-center shrink-0">
            <div className={`text-4xl font-black ${scoreColor}`}>{securityScore}</div>
            <div className="text-xs text-zinc-500 bracket-tag mt-0.5">/ 100</div>
            <div className="bracket-tag text-zinc-500 text-xs mt-1">SECURITY SCORE</div>
          </div>
        </div>
      </div>

      <div className="glass-card border border-zinc-800 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icon name="ShieldExclamationIcon" size={16} className="text-primary shrink-0" />
          <div>
            <p className="bracket-tag text-zinc-400 text-xs mb-0.5">[CROSS-REFERENCE]</p>
            <p className="text-sm text-foreground font-medium">Check this domain against the ForensicDesk Threat Registry</p>
          </div>
        </div>
        <a
          href={`/threat-registry?q=${encodeURIComponent(result.query)}`}
          className="flex items-center gap-2 border border-primary/50 hover:border-primary text-primary hover:bg-primary/5 px-4 py-2 bracket-tag transition-all duration-200 whitespace-nowrap text-xs shrink-0 min-h-[44px]"
        >
          <Icon name="MagnifyingGlassIcon" size={12} />
          SEARCH THREAT REGISTRY
        </a>
      </div>

      <div className="glass-card border border-zinc-800 p-5">
        <SectionHeader tag="[CHECKS]" title="SECURITY INDICATORS" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'HTTPS', value: hasHttps, icon: hasHttps ? '✓' : '✗' },
            { label: 'SSL VALID', value: sslValid, icon: sslValid ? '✓' : '✗' },
            { label: 'RISK SCORE', value: null, display: ipqs?.riskScore != null ? String(ipqs.riskScore) : 'N/A' },
            { label: 'DOMAIN AGE', value: null, display: domainAge },
          ].map((item) => (
            <div key={item.label} className="bg-zinc-900/60 border border-zinc-800 p-3 text-center">
              {item.value !== null ? (
                <p className={`text-2xl font-black ${item.value ? 'text-green-400' : 'text-red-400'}`}>{item.icon}</p>
              ) : (
                <p className="text-lg font-black text-zinc-300 leading-tight">{item.display}</p>
              )}
              <p className="bracket-tag text-zinc-500 text-xs mt-1">{item.label}</p>
            </div>
          ))}
        </div>

        {riskFlags.length > 0 && (
          <div className="mb-4">
            <p className="bracket-tag text-zinc-500 text-xs mb-2">IPQS RISK FLAGS</p>
            <div className="flex flex-wrap gap-1.5">
              {riskFlags.map((f) => (
                <span key={f} className="bracket-tag text-red-400 text-xs border border-red-900/50 px-2 py-0.5">[{f}]</span>
              ))}
            </div>
          </div>
        )}

        {vt && (
          <div className="border-t border-zinc-800 pt-4 mb-4">
            <p className="bracket-tag text-zinc-500 text-xs mb-3">ENGINE VERDICTS ({vt.totalEngines} ENGINES)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'MALICIOUS', value: vt.malicious, color: 'text-red-400' },
                { label: 'SUSPICIOUS', value: vt.suspicious, color: 'text-yellow-400' },
                { label: 'HARMLESS', value: vt.harmless, color: 'text-green-400' },
                { label: 'UNDETECTED', value: vt.undetected, color: 'text-zinc-400' },
              ].map((s) => (
                <div key={s.label} className="bg-zinc-900/60 border border-zinc-800 p-3 text-center">
                  <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
                  <p className="bracket-tag text-zinc-500 text-xs mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {ipqs?.domain && <DataRow label="DOMAIN" value={ipqs.domain} mono />}
        {ipqs?.ipAddress && <DataRow label="IP ADDRESS" value={ipqs.ipAddress} mono />}
        {ipqs?.country && <DataRow label="COUNTRY" value={ipqs.country} />}
        {ipqs && <DataRow label="SERVER" value={ipqs.server ?? 'N/A'} />}
        {ipqs && <DataRow label="PAGE TITLE" value={ipqs.pageTitle ?? 'N/A'} />}
        {ipqs && <DataRow label="CATEGORY" value={ipqs.category ?? 'N/A'} />}
        {ipqs && <DataRow label="STATUS CODE" value={ipqs.statusCode != null ? String(ipqs.statusCode) : 'N/A'} mono />}
      </div>

      {positiveIndicators.length > 0 && (
        <div className="glass-card border border-green-900/30 bg-green-950/5 p-5">
          <SectionHeader tag="[POSITIVE]" title="POSITIVE INDICATORS" />
          <div className="space-y-2">
            {positiveIndicators.map((ind, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-green-400 font-black text-sm shrink-0">✓</span>
                <span className="text-sm text-zinc-300">{ind}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`glass-card border ${isMalicious ? 'border-red-900/50 bg-red-950/10' : 'border-zinc-800'} p-5`}>
        <SectionHeader tag="[VERDICT]" title="RECOMMENDATION" />
        <p className={`text-sm font-semibold ${isMalicious ? 'text-red-300' : isSuspicious ? 'text-yellow-300' : 'text-zinc-300'}`}>
          {recommendation}
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        {(ipqs?.domain || result.query) && (() => {
          const domain = ipqs?.domain || (() => {
            try { return new URL(result.query).hostname; } catch { return result.query; }
          })();
          return (
            <a href={`https://who.is/whois/${domain}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-zinc-700 hover:border-primary text-zinc-400 hover:text-foreground px-4 py-2 bracket-tag transition-colors duration-200 whitespace-nowrap text-xs">
              <Icon name="ArrowTopRightOnSquareIcon" size={12} />
              FULL WHOIS REPORT
            </a>
          );
        })()}
        {vt?.vtLink && (
          <a href={vt.vtLink} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 border border-zinc-700 hover:border-primary text-zinc-400 hover:text-foreground px-4 py-2 bracket-tag transition-colors duration-200 whitespace-nowrap text-xs">
            <Icon name="ArrowTopRightOnSquareIcon" size={12} />
            ENGINE REPORT
          </a>
        )}
      </div>
    </div>
  );
}

function EmailResultPanel({ result }: { result: EmailResult }) {
  const riskLevel = result.riskLevel || 'LOW';
  const riskColorClass = riskLevel === 'HIGH' ? 'text-red-400' : riskLevel === 'MEDIUM' ? 'text-yellow-400' : 'text-green-400';
  const riskPulse = riskLevel === 'HIGH' ? 'bg-red-400' : riskLevel === 'MEDIUM' ? 'bg-yellow-400' : 'bg-green-400';

  return (
    <div className="space-y-6">
      <div className="glass-card border border-zinc-800 p-5">
        <div className="flex items-center gap-3 mb-4">
          <span className={`w-2 h-2 rounded-full ${riskPulse} threat-pulse`} />
          <span className={`bracket-tag ${riskColorClass} text-base font-black`}>RISK: {riskLevel}</span>
        </div>
        <div className="flex flex-wrap gap-3">
          <StatusBadge label="SPF" pass={result.spf === 'pass'} />
          <StatusBadge label="DKIM" pass={result.dkim === 'pass'} />
          <StatusBadge label="DMARC" pass={result.dmarc === 'pass'} />
        </div>
      </div>

      {result.anomalies && result.anomalies.length > 0 && (
        <div className="glass-card border border-red-900/40 bg-red-950/10 p-5">
          <SectionHeader tag="[ANOMALIES]" title="DETECTED ISSUES" />
          <div className="space-y-2">
            {result.anomalies.map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                <Icon name="ExclamationTriangleIcon" size={14} className="text-red-400 shrink-0 mt-0.5" />
                <span className="text-sm text-red-300">{a}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass-card border border-zinc-800 p-5">
        <SectionHeader tag="[HEADERS]" title="PARSED FIELDS" />
        <DataRow label="FROM" value={result.from} mono />
        <DataRow label="TO" value={result.to} mono />
        <DataRow label="SUBJECT" value={result.subject} />
        <DataRow label="DATE" value={result.date} />
        <DataRow label="REPLY-TO" value={result.replyTo} mono />
        <DataRow label="RETURN-PATH" value={result.returnPath} mono />
        <DataRow label="SENDER IP" value={result.senderIp} mono />
        <DataRow label="X-MAILER" value={result.xMailer} />
        <DataRow label="MESSAGE-ID" value={result.messageId} mono />
      </div>

      {result.receivedHops && result.receivedHops.length > 0 && (
        <div className="glass-card border border-zinc-800 p-5">
          <SectionHeader tag="[ROUTING]" title={`${result.receivedHops.length} HOPS`} />
          <div className="space-y-3">
            {result.receivedHops.map((hop, i) => (
              <div key={i} className="border border-zinc-800 p-3 bg-zinc-950/40">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-xs text-zinc-600 font-black">HOP {result.receivedHops!.length - i}</span>
                  {hop.ip && <span className="font-mono text-xs text-primary">{hop.ip}</span>}
                </div>
                {hop.from && <p className="text-xs text-zinc-400 font-mono">FROM: {hop.from}</p>}
                {hop.by && <p className="text-xs text-zinc-400 font-mono">BY: {hop.by}</p>}
                {hop.timestamp && <p className="text-xs text-zinc-500 mt-1">{hop.timestamp}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Client Component ────────────────────────────────────────────────────

function VerifyContent() {
  const searchParams = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [inputType, setInputType] = useState<InputType>(() => detectInputType(initialQuery));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult>(null);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialQuery) {
      runAnalysis(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQueryChange = (val: string) => {
    setQuery(val);
    setInputType(detectInputType(val));
    setResult(null);
    setError(null);
  };

  const runAnalysis = async (q?: string) => {
    const target = (q ?? query).trim();
    if (!target) return;

    const type = detectInputType(target);
    setInputType(type);
    setLoading(true);
    setResult(null);
    setError(null);

    window.history.replaceState(null, '', `/verify?q=${encodeURIComponent(target)}`);

    try {
      let endpoint = '';
      if (type === 'crypto') endpoint = '/api/verify/crypto';
      else if (type === 'url') endpoint = '/api/verify/url';
      else if (type === 'email') endpoint = '/api/verify/email';
      else {
        setError('Could not determine input type. Please enter a wallet address, transaction hash, URL, or email headers.');
        setLoading(false);
        return;
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: target }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Analysis failed. Please try again.');
      } else {
        setResult(data);
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const isCryptoResult = (r: AnalysisResult): r is CryptoResult =>
    r !== null && 'riskLevel' in r && 'summary' in r && 'details' in r && 'provider' in r;

  const typeLabel: Record<InputType, string> = {
    crypto: '[BLOCKCHAIN // CRYPTO]',
    url: '[URL // DOMAIN]',
    email: '[EMAIL HEADER]',
    unknown: '[AWAITING INPUT]',
  };

  const typeLabelColor: Record<InputType, string> = {
    crypto: 'text-blue-400',
    url: 'text-yellow-400',
    email: 'text-purple-400',
    unknown: 'text-zinc-600',
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background">
        <div className="fixed inset-0 noise-overlay pointer-events-none z-0 opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 grid-line-bg opacity-20 z-0 pointer-events-none" aria-hidden="true" />

        <section className="relative z-10 pt-24 pb-10 md:pt-32 md:pb-14 border-b border-border px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-primary" />
              <span className="bracket-tag text-primary">[LIVE INTELLIGENCE // VERIFY // FORENSICDESK]</span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <div>
                <h1 className="text-4xl md:text-6xl font-black tracking-tighter uppercase text-foreground leading-tight mb-3">
                  Intelligence<br />
                  <span className="text-gradient-crimson">Terminal.</span>
                </h1>
                <p className="text-muted-foreground font-light max-w-xl leading-relaxed">
                  <span className="text-foreground font-semibold">Real-time intelligence lookup.</span> Paste any wallet address, transaction hash, domain, or email header. We query the source — you get the intelligence.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {[
                  { label: 'BTC / ETH / LTC / DOGE / USDT +MORE', color: 'border-blue-800 text-blue-400' },
                  { label: 'URL ANALYSIS', color: 'border-yellow-800 text-yellow-400' },
                  { label: 'EMAIL HEADERS', color: 'border-purple-800 text-purple-400' },
                  { label: 'TX HASHES', color: 'border-zinc-700 text-zinc-400' },
                ].map((t) => (
                  <span key={t.label} className={`bracket-tag text-xs border px-3 py-1 ${t.color}`}>{t.label}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="relative z-10 py-10 md:py-14 px-4 border-b border-border">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className={`bracket-tag text-xs transition-colors duration-300 ${typeLabelColor[inputType]}`}>
                {typeLabel[inputType]}
              </span>
              {inputType !== 'unknown' && (
                <span className="w-1.5 h-1.5 rounded-full bg-current threat-pulse" style={{ color: 'inherit' }} />
              )}
            </div>

            <div className={`border transition-all duration-300 ${focused ? 'border-primary bg-card' : 'border-border bg-input'}`}>
              <textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && inputType !== 'email') {
                    e.preventDefault();
                    runAnalysis();
                  }
                }}
                placeholder={`Paste a wallet address, transaction hash, website URL, or email headers...\n\nExamples:\n  1A1zP1eP5QGefi2DMPTfTL5SLmv7Divf  (BTC address)\n  0x742d35Cc6634C0532925a3b844Bc454e4438f44e  (ETH address)\n  https://suspicious-site.com  (URL)\n  Received: from mail.example.com...  (Email headers)`}
                rows={6}
                className="w-full bg-transparent text-sm text-foreground placeholder:text-zinc-600 outline-none terminal-text p-4 resize-none leading-relaxed"
                aria-label="Intelligence lookup input"
              />
              <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
                <div className="flex items-center gap-2 text-xs text-zinc-600">
                  <Icon name="ShieldCheckIcon" size={12} />
                  <span className="bracket-tag">QUERIES NOT STORED // SERVER-SIDE ONLY</span>
                </div>
                <button
                  onClick={() => runAnalysis()}
                  disabled={!query.trim() || loading}
                  className="shine-effect flex items-center gap-2 bg-primary hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground px-6 py-2 bracket-tag transition-colors duration-200 text-sm"
                >
                  {loading ? (
                    <>
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground animate-pulse" />
                      ANALYZING...
                    </>
                  ) : (
                    <>
                      <Icon name="MagnifyingGlassIcon" size={13} />
                      RUN ANALYSIS
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="bracket-tag text-zinc-600 text-xs">EXAMPLES:</span>
              {INPUT_EXAMPLES.map((ex) => (
                <button
                  key={ex.label}
                  onClick={() => handleQueryChange(ex.value)}
                  className="bracket-tag text-xs text-zinc-500 hover:text-zinc-300 border border-zinc-800 hover:border-zinc-600 px-2.5 py-1 transition-colors duration-200"
                >
                  {ex.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section ref={resultsRef} className="relative z-10 py-10 md:py-16 px-4 min-h-[300px]">
          <div className="max-w-4xl mx-auto">

            {loading && (
              <div className="flex flex-col items-center justify-center py-20 gap-6">
                <div className="relative">
                  <div className="w-16 h-16 border border-zinc-800 rounded-full" />
                  <div className="absolute inset-0 w-16 h-16 border-t border-primary rounded-full animate-spin" />
                </div>
                <div className="text-center">
                  <p className="bracket-tag text-primary mb-1">QUERYING INTELLIGENCE SOURCES</p>
                  <p className="text-xs text-zinc-500">Fetching live data — this may take a few seconds</p>
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="glass-card border border-red-900/50 bg-red-950/10 p-6">
                <div className="flex items-start gap-3">
                  <Icon name="ExclamationTriangleIcon" size={18} className="text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="bracket-tag text-red-400 mb-1">ANALYSIS FAILED</p>
                    <p className="text-sm text-zinc-300">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && !result && (
              <div className="py-16 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 border border-zinc-800 mb-6">
                  <Icon name="MagnifyingGlassIcon" size={24} className="text-zinc-600" />
                </div>
                <p className="bracket-tag text-zinc-500 mb-2">AWAITING INPUT</p>
                <p className="text-sm text-zinc-600 max-w-sm mx-auto">
                  Paste any wallet address, transaction hash, website URL, or email headers above to begin analysis.
                </p>
              </div>
            )}

            {!loading && !error && result && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-6 h-px bg-primary" />
                  <span className="bracket-tag text-primary text-xs">[ANALYSIS COMPLETE]</span>
                  <div className="flex-1 h-px bg-zinc-800" />
                  <button
                    onClick={() => { setResult(null); setQuery(''); setError(null); window.history.replaceState(null, '', '/verify'); }}
                    className="bracket-tag text-xs text-zinc-500 hover:text-foreground transition-colors flex items-center gap-1"
                  >
                    <Icon name="XMarkIcon" size={12} />
                    CLEAR
                  </button>
                </div>

                {isCryptoResult(result) ? (
                  <CryptoResultPanel result={result} />
                ) : (result as UrlResult).type === 'url' ? (
                  <UrlResultPanel result={result as UrlResult} />
                ) : (result as EmailResult).type === 'email' ? (
                  <EmailResultPanel result={result as EmailResult} />
                ) : null}
              </div>
            )}

            {!loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12 text-left max-w-2xl mx-auto">
                {[
                  {
                    icon: 'CurrencyDollarIcon' as const,
                    tag: '[BLOCKCHAIN]',
                    title: 'Crypto Wallets & Transactions',
                    desc: 'BTC via ForensicDesk, ETH/USDT/USDC via ForensicDesk, LTC/DOGE/BCH via ForensicDesk. Balance, activity, and explorer links.',
                    color: 'text-blue-400',
                  },
                  {
                    icon: 'GlobeAltIcon' as const,
                    tag: '[URL SCAN]',
                    title: 'Website & Domain Analysis',
                    desc: 'Security score, risk level, SSL status, domain age, and threat analysis across 70+ security engines via ForensicDesk.',
                    color: 'text-yellow-400',
                  },
                  {
                    icon: 'EnvelopeIcon' as const,
                    tag: '[EMAIL]',
                    title: 'Email Header Forensics',
                    desc: 'SPF/DKIM/DMARC validation, routing path analysis, sender IP extraction, and spoofing detection.',
                    color: 'text-purple-400',
                  },
                  {
                    icon: 'HashtagIcon' as const,
                    tag: '[TX HASH]',
                    title: 'Transaction Hash Lookup',
                    desc: 'Block confirmation, fee data, input/output breakdown, and direct blockchain explorer link.',
                    color: 'text-zinc-400',
                  },
                ].map((cap) => (
                  <div key={cap.tag} className="group glass-card border border-zinc-800 p-4 hover:border-zinc-700 transition-colors duration-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name={cap.icon} size={14} className={cap.color} />
                      <span className={`bracket-tag text-xs ${cap.color}`}>{cap.tag}</span>
                    </div>
                    <p className="text-sm font-bold text-foreground mb-1">{cap.title}</p>
                    <p className="text-xs text-zinc-500 leading-relaxed">{cap.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {!loading && (
              <div className="mt-10 max-w-2xl mx-auto">
                <div className="glass-card border border-red-900/40 bg-red-950/10 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-red-800/60 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon name="ExclamationTriangleIcon" size={18} className="text-red-400" />
                    </div>
                    <div>
                      <p className="bracket-tag text-red-400 mb-1">[REPORT A SCAM INCIDENT]</p>
                      <p className="text-sm font-bold text-foreground mb-1">Been targeted? Don&apos;t stay silent.</p>
                      <p className="text-xs text-zinc-500 leading-relaxed">
                        If you&apos;ve encountered a fraudulent wallet, suspicious site, or scam operation — report it. Your submission helps protect others and feeds our intelligence database.
                      </p>
                    </div>
                  </div>
                  <a
                    href="/intake"
                    className="shine-effect flex items-center gap-2 bg-primary hover:bg-red-700 text-primary-foreground px-5 py-2.5 bracket-tag transition-colors duration-200 whitespace-nowrap text-sm shrink-0"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground threat-pulse" />
                    REPORT INCIDENT
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default function VerifyClient() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bracket-tag text-zinc-500">LOADING VERIFY MODULE...</div>
      </div>
    }>
      <VerifyContent />
    </Suspense>
  );
}
